'use client';

import { fetchCustomerOrder } from '@/api/account';
import { IOrder } from '@/api/types/order';
import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import { CircularProgress, InputAdornment, TextField } from '@mui/material';
import { DeleteCircleFilled2, SearchOutlined } from '@zeelool/icon';
import { useCallback, useEffect, useState } from 'react';
import OrderItem from './OrderItem';
import { useTranslations } from 'next-intl';

interface OrderSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (order: IOrder | null) => void;
  selectedOrder?: IOrder | null;
}

export default function OrderSelector({
  open,
  onClose,
  onSelect,
  selectedOrder,
}: OrderSelectorProps) {
  const t = useTranslations('contact-zeelool.OrderSelector');
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [allOrders, setAllOrders] = useState<IOrder[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchFocus, setSearchFocus] = useState(false);

  const token = useStore((state) => state.token);

  // 获取订单列表
  const fetchOrders = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const response = await fetchCustomerOrder({
        limit: 200,
        page: 1,
      });

      const orderData = response.data?.data || [];
      setAllOrders(orderData);
      setOrders(orderData);
    } catch {
      setAllOrders([]);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 处理搜索
  const handleSearch = () => {
    if (!searchText.trim()) {
      setOrders(allOrders);
    } else {
      const filteredOrders = allOrders.filter((order) =>
        order.order_no?.toLowerCase().includes(searchText.toLowerCase()),
      );
      setOrders(filteredOrders);
    }
  };

  // 处理订单选择
  const handleSelectOrder = (order: IOrder) => {
    onSelect(order);
    onClose();
  };

  // 重置搜索并获取订单列表
  const resetAndFetch = () => {
    setSearchText('');
    setAllOrders([]);
    setOrders([]);
    fetchOrders();
  };

  // 打开弹窗时获取订单列表
  useEffect(() => {
    if (open && token) {
      resetAndFetch();
    }
  }, [open, token]);

  // PC端内容
  const renderPCContent = () => {
    // 渲染订单列表的不同状态
    const renderOrderList = () => {
      if (loading) {
        return (
          <div className="text-center mt-12">
            <CircularProgress />
          </div>
        );
      }

      if (orders.length === 0) {
        return (
          <div className="my-6.5 mx-auto text-center">
            <Image
              className="block mx-auto"
              src="/contact-zeelool/no-data.png"
              alt=""
              width={100}
              height={50}
            />
            <span className="block pt-3.5 leading-4 text-14 font-normal text-secondary-text">
              {t('noResult')}
            </span>
          </div>
        );
      }

      return orders.map((order) => (
        <OrderItem
          key={order.id}
          order={order}
          isSelected={selectedOrder?.id === order.id}
          onSelect={handleSelectOrder}
          isMobile={false}
        />
      ));
    };

    return (
      <div>
        <div className="flex justify-between items-center px-40">
          <span className="font-bb text-20 font-normal text-primary-text leading-5">
            {t('selectOrder')}
          </span>
          <TextField
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('searchOrderNo')}
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loading}
                    sx={{
                      minWidth: '64px',
                      height: '40px',
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SearchOutlined style={{ width: '20px', height: '20px' }} />
                    )}
                  </Button>
                </InputAdornment>
              ),
            }}
            sx={{
              ml: 'auto',
              width: '340px',
              '& .MuiOutlinedInput-root': {
                height: '40px',
                '& input': {
                  borderRadius: '8px 0 0 8px',
                },
                paddingRight: 0,
              },
            }}
          />
        </div>
        <div className="overflow-y-auto mt-10 px-40 pb-40 max-h-[60vh]">{renderOrderList()}</div>
      </div>
    );
  };

  // 移动端内容
  const renderMobileContent = () => {
    // 渲染移动端订单列表的不同状态
    const renderMobileOrderList = () => {
      if (loading) {
        return (
          <div className="text-center mt-12">
            <CircularProgress />
          </div>
        );
      }

      if (orders.length === 0) {
        return (
          <div className="flex flex-col justify-center items-center py-60">
            <Image
              src="/contact-zeelool/no-data.png"
              alt=""
              className="w-[213px] h-[103px]"
              width={213}
              height={103}
            />
            <p className="px-43 mt-16 text-14 font-normal text-primary-text leading-17 text-center">
              {t('noOrder')}
            </p>
          </div>
        );
      }

      return (
        <div className="overflow-y-auto">
          <div className="flex-1 mt-16 px-12 pb-12">
            {orders.map((order) => (
              <OrderItem
                key={order.id}
                order={order}
                isSelected={selectedOrder?.id === order.id}
                onSelect={handleSelectOrder}
                isMobile
              />
            ))}
          </div>
        </div>
      );
    };

    return (
      <div className="flex flex-col h-[74vh] rounded-t-20 ">
        {/* 搜索框 */}
        <TextField
          value={searchText}
          onFocus={() => setSearchFocus(true)}
          onBlur={() => setSearchFocus(false)}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={t('searchOrderNo')}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            endAdornment: (
              <>
                <span className={`text-[14px] inline-flex`} onClick={() => setSearchText('')}>
                  <DeleteCircleFilled2
                    className={!(searchFocus && searchText) ? 'invisible' : 'visible'}
                  />
                </span>
                <InputAdornment position="end">
                  <div
                    className="flex items-center justify-center w-[55px] h-[32px] rounded-16 bg-primary-purple text-white cursor-pointer mr-4"
                    onClick={handleSearch}
                  >
                    {loading ? <CircularProgress size={18} color="inherit" /> : <SearchOutlined />}
                  </div>
                </InputAdornment>
              </>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              margin: 0,
              height: '40px',
              borderRadius: '20px',
              paddingRight: 0,

              backgroundColor: '#ffffff',
              '& fieldset': {
                borderColor: '#e0e0e0',
              },
              '&:focus-within': {
                '& fieldset': {
                  borderColor: '#463AE8',
                },
              },
            },
          }}
        />

        {/* 订单列表区域 */}
        {renderMobileOrderList()}
      </div>
    );
  };

  return (
    <>
      {/* PC端弹窗 */}
      <WebOnly>
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          footer={null}
          sx={{
            '& .MuiDialog-paper': { maxWidth: '1000px' },
            '& .MuiDialogContent-root': { padding: 0 },
          }}
        >
          {renderPCContent()}
        </Dialog>
      </WebOnly>

      {/* 移动端弹窗 */}
      <WapOnly>
        <Popup
          open={open}
          onClose={onClose}
          position="bottom"
          height="74vh"
          className="p-0"
          title={t('selectOrder')}
          sx={{
            '& .MuiDrawer-paper': {
              backgroundColor: '#f5f5f5',
              borderRadius: '20px 20px 0 0',
            },
            '& .popup-content': {
              padding: 0,
            },
            '& .MuiFormControl-root': {
              padding: '0 12px',
            },
          }}
        >
          {renderMobileContent()}
        </Popup>
      </WapOnly>
    </>
  );
}
